import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { ComponentType } from 'react';

export const ICON_NAMES = [
  'agreement',
  'barGraph',
  'bear',
  'book',
  'cancel',
  'caretDown',
  'caretLeft',
  'caretRight',
  'caretUp',
  'certificate',
  'checkCircle',
  'checkStopwatch',
  'clearStopwatch',
  'clock',
  'close',
  'compass',
  'cyclist',
  'directions',
  'femaleCyclist',
  'finishFlag',
  'flag',
  'glassblower',
  'graph',
  'hashTag',
  'heart',
  'hexagon',
  'hiker',
  'holdingSign',
  'home',
  'image',
  'info',
  'joy',
  'key',
  'laptop',
  'location',
  'lock',
  'map',
  'megaphone',
  'note',
  'outdoors',
  'paperPlane',
  'pdf',
  'pen',
  'pencil',
  'pieChart',
  'puzzle',
  'running',
  'search',
  'security',
  'shield',
  'signUp',
  'stop',
  'stopStopwatch',
  'target',
  'teleki',
  'tent',
  'thought',
  'trifold',
  'trophy',
  'ttt',
  'user',
  'usingLaptop',
  'usingTablet',
  'winner',
  'winnersPodium',
  'wrench',
  'xml',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

/** Loaded on demand; `wrench` is static-only (fallback in `Icon.tsx`). */
type LazyIconName = Exclude<IconName, 'wrench'>;

type IconModule = { default: ComponentType<SvgIconProps> };

const iconLoaders: Record<LazyIconName, () => Promise<IconModule>> = {
  agreement: () => import('./Agreement'),
  barGraph: () => import('./BarGraph'),
  bear: () => import('./Bear'),
  book: () => import('./Book'),
  cancel: () => import('./Cancel'),
  caretDown: () => import('./CaretDown'),
  caretLeft: () => import('./CaretLeft'),
  caretRight: () => import('./CaretRight'),
  caretUp: () => import('./CaretUp'),
  certificate: () => import('./Certificate'),
  checkCircle: () => import('./CheckCircle'),
  checkStopwatch: () => import('./CheckStopwatch'),
  clearStopwatch: () => import('./ClearStopwatch'),
  clock: () => import('./Clock'),
  close: () => import('./Close'),
  compass: () => import('./Compass'),
  cyclist: () => import('./Cyclist'),
  directions: () => import('./Directions'),
  femaleCyclist: () => import('./FemaleCyclist'),
  finishFlag: () => import('./FinishFlag'),
  flag: () => import('./Flag'),
  glassblower: () => import('./Glassblower'),
  graph: () => import('./Graph'),
  hashTag: () => import('./HashTag'),
  heart: () => import('./Heart'),
  hexagon: () => import('./Hexagon'),
  hiker: () => import('./Hiker'),
  holdingSign: () => import('./HoldingSign'),
  home: () => import('./Home'),
  image: () => import('./Image'),
  info: () => import('./Info'),
  joy: () => import('./Joy'),
  key: () => import('./Key'),
  laptop: () => import('./Laptop'),
  location: () => import('./Location'),
  lock: () => import('./Lock'),
  map: () => import('./Map'),
  megaphone: () => import('./Megaphone'),
  note: () => import('./Note'),
  outdoors: () => import('./Outdoors'),
  paperPlane: () => import('./PaperPlane'),
  pdf: () => import('./Pdf'),
  pen: () => import('./Pen'),
  pencil: () => import('./Pencil'),
  pieChart: () => import('./PieChart'),
  puzzle: () => import('./Puzzle'),
  running: () => import('./Running'),
  search: () => import('./Search'),
  security: () => import('./Security'),
  shield: () => import('./Shield'),
  signUp: () => import('./SignUp'),
  stop: () => import('./Stop'),
  stopStopwatch: () => import('./StopStopwatch'),
  target: () => import('./Target'),
  teleki: () => import('./Teleki'),
  tent: () => import('./Tent'),
  thought: () => import('./Thought'),
  trifold: () => import('./Trifold'),
  trophy: () => import('./Trophy'),
  ttt: () => import('./Ttt'),
  user: () => import('./User'),
  usingLaptop: () => import('./UsingLaptop'),
  usingTablet: () => import('./UsingTablet'),
  winner: () => import('./Winner'),
  winnersPodium: () => import('./WinnersPodium'),
  xml: () => import('./XML'),
};

const iconNameSet = new Set<string>(ICON_NAMES);

export function isIconName(value: string): value is IconName {
  return iconNameSet.has(value);
}

const iconCache = new Map<string, ComponentType<SvgIconProps>>();

export async function loadIcon(name: string): Promise<ComponentType<SvgIconProps> | null> {
  if (!isIconName(name) || name === 'wrench') {
    return null;
  }

  const cached = iconCache.get(name);
  if (cached) {
    return cached;
  }

  const mod = await iconLoaders[name]();
  iconCache.set(name, mod.default);
  return mod.default;
}

export function getCachedIcon(name: string): ComponentType<SvgIconProps> | undefined {
  return iconCache.get(name);
}
